import { FC, useEffect, useRef, useState } from "react";
import { Control, FieldValues } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiButtonSecondary,
  MuiGrid,
  MuiIconButton,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
} from "../../../../../../components";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { createProgramSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { useDispatch } from "react-redux";
import { useGetCountryListForDropdowns } from "../../../../../../api/services/client/clientApis";
import { ReactComponent as DeleteIcon } from "../../../../../../assets/images/delete-icon.svg";
import { UnitTypesKey } from "../../../../../../helpers/constants/siteCoreKeys";
import { DefaultValueExceptionType } from "../../../../../../helpers/types/common";
import { setApiDataToDropDownOption } from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { toggleToast } from "../../../../../../redux/slices/appState/appStateSlice";
import {
  CreateActivityDefaultFormKey,
  CreateActivityDefaultProps,
} from "../../../../modelsAndSchemas/Models";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
interface CreateActivityDefaultTabProps extends FieldValues {
  control: Control<CreateActivityDefaultProps>;
  pageId: string;
  rows: DefaultValueExceptionType[];
  addExceptionHandler: () => void;
  deleteAction: (countryCode: string) => void;
  selectedCountry: string;
  setSelectedCountry: React.Dispatch<React.SetStateAction<string>>;
  unitValue?: string;
}

export const CreateActivityDefault: FC<CreateActivityDefaultTabProps> = ({
  control,
  pageId,
  rows,
  addExceptionHandler,
  deleteAction,
  selectedCountry,
  setSelectedCountry,
  unitValue = "",
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const [filteredResults, setFilteredResults] = useState<
    DefaultValueExceptionType[]
  >([]);

  const dispatch = useDispatch();

  const { data: countryListData, isError } = useGetCountryListForDropdowns();

  const [selectedUnit, setSelectedUnit] = useState<string>("");
  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const [exceptionsItems, setExceptionsItems] = useState<string[]>([]);

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
  };

  useEffect(() => {
    if (isError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    setSelectedUnit(unitValue);
  }, [unitValue]);

  useEffect(() => {
    const myData = [...rows];

    myData.forEach((data) => {
      data.country =
        countryListData.find(
          (item: { key: string; value: string }) => item.key === data.country,
        )?.title || data.country;
      data.measurementName =
        data.units === UnitTypesKey.Minutes
          ? Number(
              fetchValueByKeyOfDropdown(
                createProgramSiteCoreKeys.defaultActivityKey,
                createProgramSiteCoreKeys.minutesList,
                data.measurement,
              ),
            )
          : Number(data.measurement);
    });
    setFilteredResults([...myData]);
    setExceptionsItems([...new Set(myData.map((item) => item.countryCode))]);
  }, [rows]);

  // Handle change in unit selection
  const handleUnitChange = (value: string) => {
    setSelectedUnit(value);
  };

  const renderMeasurement = (
    measurementOtherMinuteName: string,
    measurementOther: string,
  ) => {
    return (
      <>
        {selectedUnit === UnitTypesKey.Minutes && (
          <FormController
            control={control}
            name={
              measurementOtherMinuteName as keyof CreateActivityDefaultProps
            }
          >
            <MuiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.defaultActivityKey,
                createProgramSiteCoreKeys.minutes,
              )}
              options={fetchDropdownValues(
                createProgramSiteCoreKeys.defaultActivityKey,
                createProgramSiteCoreKeys.minutesList,
              )}
              isMandatory
              id={`${pageId}-${measurementOtherMinuteName}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        )}

        {(selectedUnit === UnitTypesKey.Count ||
          selectedUnit === UnitTypesKey.Days) && (
          <FormController
            control={control}
            name={measurementOther as keyof CreateActivityDefaultProps}
          >
            <MuiTextField
              label={fetchLabel(
                createProgramSiteCoreKeys.defaultActivityKey,
                selectedUnit === UnitTypesKey.Count
                  ? createProgramSiteCoreKeys.quantity
                  : createProgramSiteCoreKeys.numberofDays,
              )}
              id={`${pageId}-${measurementOther}`}
              type="number"
              placeholder={commonLabels.inputPlaceHolder}
              isMandatory
              inputNumberWithoutDecimal
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        )}
      </>
    );
  };

  const measurementKey =
    selectedUnit === UnitTypesKey.Count
      ? createProgramSiteCoreKeys.quantity
      : createProgramSiteCoreKeys.numberofDays;

  const measurementLabel =
    selectedUnit === UnitTypesKey.Minutes
      ? fetchLabel(
          createProgramSiteCoreKeys.defaultActivityKey,
          createProgramSiteCoreKeys.minutes,
        )
      : fetchLabel(
          createProgramSiteCoreKeys.defaultActivityKey,
          measurementKey,
        );

  const columns = [
    {
      id: CreateActivityDefaultFormKey.country,
      label: fetchLabel(
        createProgramSiteCoreKeys.defaultActivityKey,
        createProgramSiteCoreKeys.defaultActivityCountry,
      ),
      isSortVisible: true,
    },
    // ...(selectedUnit !== UnitTypesKey.HalfDay
    //   ? [
    {
      id: CreateActivityDefaultFormKey.measurementName,
      label: measurementLabel,
      isSortVisible: true,
    },
    //   ]
    // : []),
    {
      id: "action",
      label: fetchLabel(
        createProgramSiteCoreKeys.defaultActivityKey,
        createProgramSiteCoreKeys.action,
      ),
      render: (row: any) => {
        return (
          <MuiIconButton
            onClick={() => deleteAction(row.countryCode)}
            data-testid="delete-btn"
            aria-label={commonARIALabel.delete}
          >
            <DeleteIcon />
          </MuiIconButton>
        );
      },
    },
  ];

  return (
    <MuiBox data-testid="activity-tab-default">
      <MuiGrid container className="activity-tab-default">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={CreateActivityDefaultFormKey.units}
          >
            <MuiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.defaultActivityKey,
                createProgramSiteCoreKeys.units,
              )}
              options={fetchDropdownValues(
                createProgramSiteCoreKeys.defaultActivityKey,
                createProgramSiteCoreKeys.unitLists,
              )}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${CreateActivityDefaultFormKey.units}`}
              isMandatory
              disabled
              placeholder={commonLabels.selectPlaceHolder}
              onChange={(e) => handleUnitChange(e.target.value as string)}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          {renderMeasurement(
            CreateActivityDefaultFormKey.measurementMinute,
            CreateActivityDefaultFormKey.measurementOther,
          )}
        </MuiGrid>
      </MuiGrid>

      <MuiBox>
        <MuiTypography
          variant="h2SemiBold"
          className="exception-heading"
          component="h2"
        >
          {fetchLabel(
            createProgramSiteCoreKeys.defaultActivityKey,
            createProgramSiteCoreKeys.addException,
          )}
        </MuiTypography>
      </MuiBox>

      <MuiGrid container className="exception-form-container">
        <MuiGrid size={gridSize} className={`exception-form-field form-field`}>
          <FormController
            control={control}
            name={CreateActivityDefaultFormKey.country}
          >
            <MuiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.defaultActivityKey,
                createProgramSiteCoreKeys.defaultActivityCountry,
              )}
              options={setApiDataToDropDownOption(countryListData, true)}
              value=""
              id={`${pageId}-${CreateActivityDefaultFormKey.country}`}
              visibleDefaultSelectOption
              placeholder={commonLabels.selectPlaceHolder}
              onChange={(e) => handleCountryChange(e.target.value as string)}
              disabledItems={[...exceptionsItems]}
              requiredARIALabel={commonARIALabel.required}
              showSearch
              searchPlaceholder={commonLabels.search}
            />
          </FormController>
        </MuiGrid>
        {selectedUnit &&
          selectedCountry &&
          selectedUnit !== UnitTypesKey.HalfDay && (
            <MuiGrid size={gridSize} className="exception-form-field">
              {renderMeasurement(
                CreateActivityDefaultFormKey.countryMeasurementMinute,
                CreateActivityDefaultFormKey.countryMeasurementOther,
              )}
            </MuiGrid>
          )}
        <MuiGrid>
          <MuiButtonSecondary
            onClick={addExceptionHandler}
            className="add-button"
            id="add-exception-btn"
          >
            {commonLabels.add}
          </MuiButtonSecondary>
        </MuiGrid>
      </MuiGrid>

      <MuiBox className="table-container">
        <MuiGrid container data-testid="activity-exception">
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={[]}
              columns={columns}
              tableResults={[...filteredResults]}
              noDataInApi={rows.length === 0}
              noResultMessage={commonLabels.noResult}
              emptyMessage={""}
              priorityResults={[]}
              isLastColumnSticky
              // footerColSpan={selectedUnit === UnitTypesKey.HalfDay ? 1 : 2}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
    </MuiBox>
  );
};
